"use client";

import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  useReducer,
} from "react";
import { useTranslations, useLocale } from "next-intl";
import { checkoutMarketingContext } from "@/lib/tracking/marketing-events";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalTitle,
} from "@/components/ui/responsive-modal";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { XIcon } from "lucide-react";
import {
  cleanPhoneNumber,
  getMobileDigitCount,
  MOBILE_MAX_LENGTH,
} from "@/lib/utils/phone";
import { formatInteger } from "@/lib/utils";
import { getErrorMessage, isAuthError } from "@/lib/utils/error-handler";
import { resolveLoginErrorMessage } from "@/lib/utils/auth-error";
import type {
  MoyasarCardFieldErrors,
  MoyasarInitResponse,
  MoyasarTokenizeCardParams,
} from "@/lib/utils/moyasar";
import {
  createSavedCardMoyasarPayment,
  MoyasarTokenizeError,
  tokenizeCardToMoyasar,
} from "@/lib/utils/moyasar";
import { useAuthStore } from "@/store/auth";
import { useAssociationStore } from "@/store/association";
import { getAssociationId } from "@/config/association";
import { usePortalFeatures } from "@/lib/portalFeatures";
import { authService, bankService, paymentService } from "@/lib/api/services";
import {
  DEFAULT_DONATION_SUCCESS_SETTINGS,
  localizedSetting,
  type DonationSuccessSettings,
} from "@/lib/donationSuccessSettings";
import { useCartStore } from "@/store/cart";
import { cartRequiresTokenizedPayment } from "@/components/cart/cartUtils";
import { useTracker } from "@/components/providers/TrackerProvider";
import type { Value } from "react-phone-number-input";
import Image from "next/image";
import { SarIcon } from "@/components/icons/SarIcon";
import { EnumPaymentMethods } from "@/types";
import type {
  BankWithAccounts,
  ProcessCartItem,
  ShippingAddress,
} from "@/types";
// buildBankTransferProcessCartRequest no longer needed — bank transfer goes through Next.js API route

import { usePrepareDonationPayment } from "./usePrepareDonationPayment";
import { useShippingAddressForm } from "./useShippingAddressForm";
import { ShippingAddressFields } from "./ShippingAddressFields";
import {
  donationModalReducer,
  initialDonationModalState,
  type ModalView,
} from "./donationModalReducer";
import { DonationPhoneStep } from "./steps/DonationPhoneStep";
import { DonationAuthStep } from "./steps/DonationAuthStep";
import { DonationPaymentStep } from "./steps/DonationPaymentStep";
import { DonationSuccessStep } from "./steps/DonationSuccessStep";
import type { UnifiedDonationProjectParams } from "./unifiedDonationTypes";

export type { UnifiedDonationProjectParams } from "./unifiedDonationTypes";

type PaymentGatewayFlags = {
  activeVisaCardMethod?: boolean;
  activeCardPaymentMethod?: boolean;
  activeBankTransferMethod?: boolean;
  activePaymentOnline?: boolean;
};

type PaymentGatewayFallback = {
  ActiveVisaCardMethod?: boolean | null;
  ActiveBankTransferMethod?: boolean | null;
} | null;

export function resolvePaymentMethodAvailability({
  paymentGatewayFlags,
  association,
  requiresTokenizedPayment = false,
}: {
  paymentGatewayFlags: PaymentGatewayFlags | null;
  association: PaymentGatewayFallback;
  /**
   * Set for recurring deductions: later installments are charged against a
   * saved card token, which only the online gateway (Moyasar / Apple Pay) can
   * issue, so a manual bank transfer cannot fulfil the agreement.
   */
  requiresTokenizedPayment?: boolean;
}) {
  const bankTransferEnabled =
    !requiresTokenizedPayment &&
    (paymentGatewayFlags?.activeBankTransferMethod ??
      association?.ActiveBankTransferMethod ??
      true);
  const cardEnabled =
    paymentGatewayFlags == null
      ? (association?.ActiveVisaCardMethod ?? true)
      : paymentGatewayFlags.activePaymentOnline === true &&
        (paymentGatewayFlags.activeVisaCardMethod === true ||
          paymentGatewayFlags.activeCardPaymentMethod === true);

  return {
    bankTransferEnabled,
    cardEnabled,
    hasPaymentMethodEnabled: bankTransferEnabled || cardEnabled,
  };
}

/** Pre-prepared payment (e.g. from cart page) so modal can show form immediately */
export interface PreparedPayment {
  operationCode: string;
  moyasarConfig: MoyasarInitResponse;
}

/** Fallback image for donation overview card when no project image (exists in public) */
const OVERVIEW_FALLBACK_IMAGE = "/icons/bag-happy.svg";

// ─── Props ──────────────────────────────────────────────────────────────────
export interface UnifiedDonationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (operationCode: string) => void;
  /** Single project fast-pay params (product page / quick donation) */
  projectParams?: UnifiedDonationProjectParams | null;
  /** Cart checkout mode – requires login, processes cart items */
  cartMode?: boolean;
  /** When true, phone step collects identity only (no auth forms) for Apple Pay guest checkout. */
  guestPhoneOnlyApplePay?: boolean;
  /**
   * Cart mode: skip the phone/login step for guests — the mobile is collected
   * inside the payment form and a faster-login runs on payment submit
   * (mirrors the guest quick-donation experience).
   */
  guestPhoneInPaymentForm?: boolean;
  /** Pre-prepared payment (cart page prepares in background); modal uses it when open so no API wait */
  preparedPayment?: PreparedPayment | null;
}

type CheckoutIntentState =
  | "idle"
  | "preparing"
  | "ready"
  | "paying"
  | "success"
  | "failed";

function normalizeFlag(value: unknown): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    return v === "true" || v === "1";
  }
  return false;
}

/** Validates phone field and sets form errors; returns cleaned phone or null if invalid. */
function validatePhoneAndSetErrors(
  form: {
    getValues: (name: "mobile") => Value;
    setError: (name: "mobile", opts: { message: string }) => void;
  },
  t: (key: string) => string,
): string | null {
  const mobile = form.getValues("mobile");
  if (!mobile) {
    form.setError("mobile", { message: t("auth.mobileRequired") });
    return null;
  }
  const cleaned = cleanPhoneNumber(mobile);
  if (getMobileDigitCount(cleaned) < 9) {
    form.setError("mobile", { message: t("auth.invalidMobile") });
    return null;
  }
  if (getMobileDigitCount(cleaned) > MOBILE_MAX_LENGTH) {
    form.setError("mobile", { message: t("auth.mobileMaxLength") });
    return null;
  }
  return cleaned;
}

/**
 * Initial modal view for donors without a full session: cart mode needs the
 * phone/login step unless the caller opted into collecting the phone inside
 * the payment form; guest single-project always pays directly.
 */
export function resolveInitialCheckoutView({
  isFullSession,
  cartMode,
  hasProjectParams,
  guestCartPhoneInForm,
}: {
  isFullSession: boolean;
  cartMode: boolean;
  hasProjectParams: boolean;
  guestCartPhoneInForm: boolean;
}): "payment" | "phone" {
  if (isFullSession) return "payment";
  if (cartMode) return guestCartPhoneInForm ? "payment" : "phone";
  return hasProjectParams ? "payment" : "phone";
}

// ─── Component ──────────────────────────────────────────────────────────────
export function UnifiedDonationModal({
  isOpen,
  onClose,
  onSuccess,
  projectParams,
  cartMode = false,
  guestPhoneOnlyApplePay = false,
  guestPhoneInPaymentForm = false,
  preparedPayment = null,
}: UnifiedDonationModalProps) {
  const t = useTranslations();
  const locale = useLocale();
  const {
    isAuthenticated,
    user,
    isLoading: isAuthLoading,
    sessionHydrated,
    authSessionKind,
    loginFaster,
  } = useAuthStore();
  const { trackDonation } = useTracker();
  const portalFeatures = usePortalFeatures();
  const association = useAssociationStore((s) => s.association);
  const storeAssociationId = useAssociationStore((s) => s.associationId);
  const associationId =
    storeAssociationId && storeAssociationId > 0
      ? storeAssociationId
      : getAssociationId();
  const [paymentGatewayFlags, setPaymentGatewayFlags] =
    useState<PaymentGatewayFlags | null>(null);
  const cartHasDeduction = useCartStore((s) =>
    cartRequiresTokenizedPayment(s.items),
  );
  // Deductions are charged again later against a saved card token, so the
  // donor must go through the online gateway on this first payment.
  const requiresTokenizedPayment = cartMode
    ? cartHasDeduction
    : projectParams?.isRecurring === true;
  const { bankTransferEnabled, cardEnabled, hasPaymentMethodEnabled } =
    resolvePaymentMethodAvailability({
      paymentGatewayFlags,
      association,
      requiresTokenizedPayment,
    });
  const bankTransferAvailable = bankTransferEnabled;

  // ── View + payment state (reducer) ───────────────────────────────────────
  const [state, dispatch] = useReducer(
    donationModalReducer,
    initialDonationModalState,
  );
  const {
    view,
    phoneNumber,
    error,
    paymentStatus,
    paymentError,
    operationCode,
    donationId,
    moyasarConfig,
  } = state;

  const setView = useCallback((view: ModalView) => {
    dispatch({ type: "SET_VIEW", view });
  }, []);
  const setPhoneNumber = useCallback((phoneNumber: string) => {
    dispatch({ type: "SET_PHONE", phoneNumber });
  }, []);
  const setError = useCallback((error: string | null) => {
    dispatch({ type: "SET_ERROR", error });
  }, []);
  const setPaymentError = useCallback((message: string | null) => {
    dispatch({ type: "SET_PAYMENT_ERROR", message });
  }, []);

  const [isCheckingPhone, setIsCheckingPhone] = useState(false);
  const [applePayPhoneOnlyActive, setApplePayPhoneOnlyActive] = useState(false);
  /** True once donation is created and Moyasar config is ready (avoids duplicate prepare). */
  const paymentPreparedRef = useRef(false);
  const [isPreparingPayment, setIsPreparingPayment] = useState(false);
  const [isQuickDonating, setIsQuickDonating] = useState(false);
  const [paymentInitKey, setPaymentInitKey] = useState(0);
  const [paymentMethodTab, setPaymentMethodTab] = useState<
    "card" | "bankTransfer"
  >("card");
  const [banksWithAccounts, setBanksWithAccounts] = useState<
    BankWithAccounts[]
  >([]);
  const [isLoadingBanks, setIsLoadingBanks] = useState(false);
  const [selectedBankId, setSelectedBankId] = useState<number | null>(null);
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(
    null,
  );
  const [donorBankText, setDonorBankText] = useState("");
  const [donorBankAccountNum, setDonorBankAccountNum] = useState("");
  const [receiptImageId, setReceiptImageId] = useState<number | null>(null);
  const [isUploadingReceipt, setIsUploadingReceipt] = useState(false);
  const [isSubmittingBankTransfer, setIsSubmittingBankTransfer] =
    useState(false);
  const [bankTransferError, setBankTransferError] = useState<string | null>(
    null,
  );
  const [isPendingReviewSuccess, setIsPendingReviewSuccess] = useState(false);
  const [successSettings, setSuccessSettings] =
    useState<DonationSuccessSettings>(DEFAULT_DONATION_SUCCESS_SETTINGS);
  const [isCardSubmitting, setIsCardSubmitting] = useState(false);
  const [cardError, setCardError] = useState<string | null>(null);
  const [cardFieldErrors, setCardFieldErrors] =
    useState<MoyasarCardFieldErrors | null>(null);
  const [checkoutIntentState, setCheckoutIntentState] =
    useState<CheckoutIntentState>("idle");
  const handlePaymentMethodTabChange = useCallback(
    (tab: "card" | "bankTransfer") => {
      setPaymentMethodTab(tab);
      setCardError(null);
      setCardFieldErrors(null);
      setBankTransferError(null);
      setPaymentError(null);
      if (paymentStatus === "error") {
        dispatch({ type: "SET_PAYMENT_IDLE" });
        setCheckoutIntentState("idle");
      }
    },
    [paymentStatus, setPaymentError],
  );

  useEffect(() => {
    if (!isOpen || !associationId) {
      setPaymentGatewayFlags(null);
      return;
    }

    let cancelled = false;
    fetch(`/api/payments/gateway/${associationId}`, {
      method: "GET",
      cache: "no-store",
    })
      .then((response) => (response.ok ? response.json() : null))
      .then(
        (
          payload: {
            success?: boolean;
            data?: PaymentGatewayFlags;
          } | null,
        ) => {
          if (!cancelled) {
            setPaymentGatewayFlags(
              payload?.success ? payload.data ?? null : null,
            );
          }
        },
      )
      .catch(() => {
        if (!cancelled) setPaymentGatewayFlags(null);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, associationId]);

  // Cart helpers (only used in cartMode)
  const cartItems = useCartStore((s) => s.items);
  const cartTotal = useCartStore((s) => s.getTotal());
  const loadCart = useCartStore((s) => s.loadCart);
  const cartLastError = useCartStore((s) => s.lastError);
  const cartIsHydrated = useCartStore((s) => s.isHydrated);
  const cartIsLoading = useCartStore((s) => s.isLoading);
  const hasProductItemsInCart =
    cartMode && cartItems.some((item) => normalizeFlag(item.IsProduct));
  const requiresShippingAddress = hasProductItemsInCart;
  const [shippingAddressData, setShippingAddressData] =
    useState<ShippingAddress | null>(null);
  const {
    isLoadingCities: isLoadingShippingCities,
    cities: shippingCities,
    error: shippingError,
    formData: shippingFormData,
    formErrors: shippingFormErrors,
    handleInputChange: handleShippingInputChange,
    validateForm: validateShippingForm,
    setError: setShippingError,
  } = useShippingAddressForm(t, {
    enabled:
      isOpen &&
      view === "payment" &&
      requiresShippingAddress &&
      shippingAddressData === null,
  });

  const isCartCheckoutReady =
    !cartMode ||
    guestPhoneOnlyApplePay ||
    (isAuthenticated &&
      !isAuthLoading &&
      sessionHydrated &&
      cartIsHydrated &&
      !cartIsLoading &&
      cartItems.length > 0);
  const checkoutInitInFlightRef = useRef(false);
  const cartRetryUsedRef = useRef(false);

  // ── Derived values ──────────────────────────────────────────────────────
  const total = projectParams
    ? projectParams.amount * (projectParams.quantity || 1)
    : cartMode
      ? cartTotal
      : 0;

  // A zero cart total is never a missing amount field: the donor either has an
  // empty cart or lines the backend refused while merging the guest cart.
  const emptyTotalReason =
    cartMode && total <= 0
      ? cartLastError === "CART_MIGRATION_INCOMPLETE"
        ? t("checkout.errors.cartMigrationIncomplete")
        : t("checkout.errors.cartEmpty")
      : null;

  const { prepareCart, prepareSingleProject, prepareSingleProjectWithDonor } =
    usePrepareDonationPayment({
      projectParams,
      cartItems,
      associationId: associationId || 0,
      total,
    });

  // ── Phone form ──────────────────────────────────────────────────────────
  const formSchema = useMemo(
    () =>
      z.object({
        mobile: z
          .custom<Value>(
            (val) => typeof val === "string" && val.trim().length > 0,
            { message: t("auth.mobileRequired") },
          )
          .refine((val) => getMobileDigitCount(cleanPhoneNumber(val || "")) >= 9, {
            message: t("auth.invalidMobile"),
          })
          .refine(
            (val) => getMobileDigitCount(val || "") <= MOBILE_MAX_LENGTH,
            {
              message: t("auth.mobileMaxLength"),
            },
          ),
      }),
    [t],
  );

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: { mobile: undefined },
  });

  const guestMobileWatched = form.watch("mobile");
  // A "fast" (quick-donation) session is checkout-only: the donor must
  // re-confirm their phone on every donation, so phone-related guards treat
  // it like a guest. Only a full login skips phone collection.
  const isFullSession = isAuthenticated && authSessionKind === "full";
  // Guest / fast-session cart checkout with the phone collected in the
  // payment form instead of the phone step (faster-login on payment submit).
  // Disabled when the portal forbids faster login — full auth is required.
  const guestCartPhoneInForm =
    cartMode &&
    guestPhoneInPaymentForm &&
    !isFullSession &&
    !portalFeatures.stopFasterLogin;
  const applePayDonorMobile = useMemo(() => {
    if (!cartMode && !projectParams) return undefined;
    const acct = isAuthenticated
      ? cleanPhoneNumber(String(user?.mobile ?? user?.PhoneNumber ?? ""))
      : "";
    if (isFullSession && getMobileDigitCount(acct) >= 9) return acct;
    const fromForm = guestMobileWatched
      ? cleanPhoneNumber(String(guestMobileWatched))
      : "";
    if (getMobileDigitCount(fromForm) >= 9) return fromForm;
    const fromPhoneStep = phoneNumber ? cleanPhoneNumber(phoneNumber) : "";
    if (getMobileDigitCount(fromPhoneStep) >= 9) return fromPhoneStep;
    // Fast session with an untouched form: fall back to the account phone.
    if (getMobileDigitCount(acct) >= 9) return acct;
    return undefined;
  }, [
    cartMode,
    projectParams,
    isAuthenticated,
    isFullSession,
    user?.mobile,
    user?.PhoneNumber,
    guestMobileWatched,
    phoneNumber,
  ]);

  const applePayDonorName = useMemo(() => {
    const fullName =
      typeof user?.FullName === "string"
        ? user.FullName
        : typeof user?.Name === "string"
          ? user.Name
          : "";
    const trimmed = fullName.trim();
    if (cartMode) {
      return trimmed || undefined;
    }
    if (!projectParams || !isAuthenticated || !user) {
      return undefined;
    }
    return trimmed || undefined;
  }, [cartMode, projectParams, isAuthenticated, user]);

  // ── Determine initial view ──────────────────────────────────────────────
  // Guest + quick/specific project: skip phone step, show payment with phone in card form
  useEffect(() => {
    if (!isOpen) return;
    if (isFullSession && user) {
      // Fully logged-in users skip straight to payment. A "fast" session
      // (quick donation) must re-confirm the phone on every donation.
      const mobile =
        typeof user.mobile === "string"
          ? user.mobile
          : typeof user.PhoneNumber === "string"
            ? user.PhoneNumber
            : "";
      setPhoneNumber(mobile);
      setView("payment");
    } else {
      setView(
        resolveInitialCheckoutView({
          isFullSession: false,
          cartMode,
          hasProjectParams: !!projectParams,
          guestCartPhoneInForm,
        }),
      );
    }
  }, [
    isOpen,
    isFullSession,
    user,
    cartMode,
    projectParams,
    guestCartPhoneInForm,
    setPhoneNumber,
    setView,
  ]);

  useEffect(() => {
    if (!isOpen) return;
    if (cardEnabled) {
      setPaymentMethodTab("card");
    } else if (bankTransferAvailable) {
      setPaymentMethodTab("bankTransfer");
    }
  }, [isOpen, cardEnabled, bankTransferAvailable]);

  // ── Apply pre-prepared payment (from cart page) so form shows immediately ─
  useEffect(() => {
    if (
      !isOpen ||
      view !== "payment" ||
      !preparedPayment ||
      !cardEnabled ||
      paymentMethodTab !== "card" ||
      requiresShippingAddress
    )
      return;
    dispatch({
      type: "SET_PAYMENT_READY",
      operationCode: preparedPayment.operationCode,
      moyasarConfig: preparedPayment.moyasarConfig,
    });
    paymentPreparedRef.current = true;
  }, [
    isOpen,
    view,
    preparedPayment,
    cardEnabled,
    paymentMethodTab,
    requiresShippingAddress,
  ]);

  // ── Auto-focus phone input ──────────────────────────────────────────────
  useEffect(() => {
    if (isOpen && view === "phone") {
      const timer = setTimeout(() => {
        const input = document.querySelector(
          '.unified-donation-modal input[type="tel"]',
        ) as HTMLInputElement;
        if (input) input.focus();
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [isOpen, view]);

  // ── Reset everything when modal closes ──────────────────────────────────
  useEffect(() => {
    if (!isOpen) {
      dispatch({ type: "RESET" });
      setIsPreparingPayment(false);
      setShippingAddressData(null);
      paymentPreparedRef.current = false;
      setBanksWithAccounts([]);
      setIsLoadingBanks(false);
      setSelectedBankId(null);
      setSelectedAccountId(null);
      setDonorBankText("");
      setDonorBankAccountNum("");
      setReceiptImageId(null);
      setIsUploadingReceipt(false);
      setIsSubmittingBankTransfer(false);
      setBankTransferError(null);
      setIsPendingReviewSuccess(false);
      setCardError(null);
      setCardFieldErrors(null);
      setIsCardSubmitting(false);
      setCheckoutIntentState("idle");
      checkoutInitInFlightRef.current = false;
      cartRetryUsedRef.current = false;
      setApplePayPhoneOnlyActive(false);
      setIsQuickDonating(false);
      form.reset();
    }
  }, [isOpen, form]);

  // ── Phone step: fast donation (no login) – call API on "Donate now", then show payment
  const handleFastDonation = useCallback(async () => {
    const cleaned = validatePhoneAndSetErrors(form, t);
    if (!cleaned) return;
    setPhoneNumber(cleaned);

    // Global quick donation: create donation and get Moyasar config before showing payment step
    if (projectParams) {
      setIsPreparingPayment(true);
      setPaymentError(null);
      try {
        const { opCode, payAmount, moyasarConfigData } =
          await prepareSingleProject(cleaned);

        if (typeof window !== "undefined") {
          sessionStorage.setItem(
            "checkout:lastPurchase",
            JSON.stringify({
              transaction_id: opCode,
              value: payAmount,
              currency: "SAR",
              phone: cleaned,
              quantity: projectParams.quantity ?? 1,
              content_ids: [String(projectParams.projectId)],
            }),
          );
        }

        dispatch({
          type: "SET_PAYMENT_READY",
          operationCode: opCode,
          moyasarConfig: moyasarConfigData,
        });
        paymentPreparedRef.current = true;
        setView("payment");
      } catch (err: unknown) {
        if (isAuthError(err)) return;
        console.error("[UnifiedDonationModal] Error:", err);
        setPaymentError(getErrorMessage(err, t("common.error"), t));
        setView("payment");
      } finally {
        setIsPreparingPayment(false);
      }
      return;
    }

    setView("payment");
  }, [
    form,
    t,
    projectParams,
    prepareSingleProject,
    setPaymentError,
    setPhoneNumber,
    setView,
  ]);

  // ── Phone step: login / register check ──────────────────────────────────
  const handleLogin = useCallback(async () => {
    const cleaned = validatePhoneAndSetErrors(form, t);
    if (!cleaned) return;

    setIsCheckingPhone(true);
    setError(null);
    const isPhoneOnly = guestPhoneOnlyApplePay || applePayPhoneOnlyActive;
    if (isPhoneOnly) {
      setPhoneNumber(cleaned);
      setView("payment");
      setIsCheckingPhone(false);
      return;
    }
    try {
      const response = await authService.checkMobile(cleaned);
      setPhoneNumber(cleaned);

      if (response.success && response.data?.action) {
        const action = response.data.action;
        if (action === "login") setView("password");
        else if (action === "register") setView("register");
        else if (action === "set-password") setView("set-password");
        else setView("register");
      } else {
        setError(response.message || response.error || t("auth.loginError"));
      }
    } catch {
      setPhoneNumber(cleaned);
      setError(t("auth.loginError"));
    } finally {
      setIsCheckingPhone(false);
    }
  }, [
    form,
    t,
    guestPhoneOnlyApplePay,
    applePayPhoneOnlyActive,
    setError,
    setPhoneNumber,
    setView,
  ]);

  // ── Phone step: quick donation (loginFaster → payment) ─────────────────
  const handleQuickDonation = useCallback(async () => {
    const cleaned = validatePhoneAndSetErrors(form, t);
    if (!cleaned) return;

    setIsQuickDonating(true);
    setError(null);

    try {
      if (portalFeatures.stopFasterLogin) {
        setPhoneNumber(cleaned);
        setError(null);
        // Fall back to full auth check instead of faster login.
        const response = await authService.checkMobile(cleaned);
        if (response.success && response.data?.action) {
          const action = response.data.action;
          if (action === "login") setView("password");
          else if (action === "set-password") setView("set-password");
          else setView("register");
        } else {
          setError(response.message || response.error || t("auth.loginError"));
        }
        return;
      }

      const result = await loginFaster(cleaned);
      if (!result.success) {
        setError(resolveLoginErrorMessage(result.message, t));
        return;
      }

      setPhoneNumber(cleaned);

      if (cartMode) {
        setView("payment");
        return;
      }

      if (projectParams) {
        setIsPreparingPayment(true);
        try {
          const { opCode, payAmount, moyasarConfigData } =
            await prepareSingleProject(cleaned);

          if (typeof window !== "undefined") {
            sessionStorage.setItem(
              "checkout:lastPurchase",
              JSON.stringify({
                transaction_id: opCode,
                value: payAmount,
                currency: "SAR",
                phone: cleaned,
                quantity: projectParams.quantity ?? 1,
                content_ids: [String(projectParams.projectId)],
              }),
            );
          }

          dispatch({
            type: "SET_PAYMENT_READY",
            operationCode: opCode,
            moyasarConfig: moyasarConfigData,
          });
          paymentPreparedRef.current = true;
          setView("payment");
        } catch (err: unknown) {
          if (isAuthError(err)) return;
          setPaymentError(getErrorMessage(err, t("common.error"), t));
          setView("payment");
        } finally {
          setIsPreparingPayment(false);
        }
        return;
      }

      setView("payment");
    } catch {
      setError(t("auth.loginError"));
    } finally {
      setIsQuickDonating(false);
    }
  }, [
    form,
    t,
    loginFaster,
    cartMode,
    projectParams,
    prepareSingleProject,
    portalFeatures.stopFasterLogin,
    setError,
    setPaymentError,
    setPhoneNumber,
    setView,
  ]);

  // ── Auth sub-form callbacks ─────────────────────────────────────────────
  const handleBackToPhone = useCallback(() => {
    setView("phone");
    setError(null);
  }, [setError, setView]);

  // ── After auth (cart): create donation and get config before showing payment step
  const handleAuthSuccess = useCallback(async () => {
    if (!cartMode) {
      setView("payment");
      return;
    }

    if (requiresShippingAddress) {
      setView("payment");
      return;
    }
    setView("payment");
  }, [cartMode, requiresShippingAddress, setView]);

  const isCartEmptyMessage = useCallback((message: string): boolean => {
    const normalized = message.trim().toLowerCase();
    return (
      normalized.includes("cart is empty") ||
      normalized.includes("your cart is empty") ||
      normalized.includes("السلة فارغة")
    );
  }, []);

  const initializeCartPayment = useCallback(async (opts?: {
    mobileOverride?: string;
    skipReadyCheck?: boolean;
  }) => {
    if (!cartMode || requiresShippingAddress) return;
    // skipReadyCheck: right after a faster-login the memoized readiness flag
    // is stale (auth state changed mid-submit), but the session cookies are
    // already set so process-cart can proceed (it syncs the guest cart).
    if (!opts?.skipReadyCheck && !isCartCheckoutReady) {
      throw new Error(t("checkout.errors.cartEmpty"));
    }
    if (checkoutInitInFlightRef.current) return;

    checkoutInitInFlightRef.current = true;
    setIsPreparingPayment(true);
    setCheckoutIntentState("preparing");
    console.info("[CheckoutFlow] checkout.intent.prepare.started", {
      mode: "cart",
      paymentMethod: "card",
      cartItemCount: cartItems.length,
    });
    setPaymentError(null);

    const rawMobile =
      opts?.mobileOverride ||
      phoneNumber ||
      (typeof user?.mobile === "string" ? user.mobile : "") ||
      (typeof user?.PhoneNumber === "string" ? user.PhoneNumber : "");
    const rawName =
      (typeof user?.name === "string" ? user.name : "") ||
      (typeof user?.Name === "string" ? user.Name : "");

    try {
      const executePrepareCart = async () => {
        const result = await prepareCart(
          rawMobile,
          rawName,
          shippingAddressData ?? undefined,
        );
        if (typeof window !== "undefined") {
          sessionStorage.setItem(
            "checkout:lastPurchase",
            JSON.stringify({
              transaction_id: result.opCode,
              value: result.payAmount,
              currency: "SAR",
              phone: rawMobile || undefined,
              quantity: cartItems.length,
              content_ids: cartItems.map((i) => String(i.ProjectId)),
            }),
          );
        }
        dispatch({
          type: "SET_PAYMENT_READY",
          operationCode: result.opCode,
          moyasarConfig: result.moyasarConfigData,
        });
        console.info("[CheckoutFlow] checkout.intent.prepare.succeeded", {
          mode: "cart",
          paymentMethod: "card",
          operationCode: result.opCode,
        });
        setCheckoutIntentState("ready");
        paymentPreparedRef.current = true;
        return result;
      };

      try {
        const result = await executePrepareCart();
        cartRetryUsedRef.current = false;
        return result;
      } catch (err) {
        if (isAuthError(err)) throw err;
        const message = getErrorMessage(err, t("common.error"), t);
        if (isCartEmptyMessage(message) && !cartRetryUsedRef.current) {
          cartRetryUsedRef.current = true;
          await loadCart();
          const retriedResult = await executePrepareCart();
          cartRetryUsedRef.current = false;
          return retriedResult;
        }
        throw err;
      }
    } catch (err: unknown) {
      if (isAuthError(err)) {
        setCheckoutIntentState("idle");
        setError(getErrorMessage(err, undefined, t));
        setView("phone");
        throw err;
      }
      console.error("[UnifiedDonationModal] Error:", err);
      console.info("[CheckoutFlow] checkout.intent.prepare.failed", {
        mode: "cart",
        paymentMethod: "card",
      });
      setCheckoutIntentState("failed");
      setPaymentError(getErrorMessage(err, t("common.error"), t));
      setView("payment");
    } finally {
      setIsPreparingPayment(false);
      checkoutInitInFlightRef.current = false;
    }
  }, [
    cartMode,
    requiresShippingAddress,
    isCartCheckoutReady,
    phoneNumber,
    user,
    prepareCart,
    shippingAddressData,
    cartItems,
    isCartEmptyMessage,
    loadCart,
    t,
    setError,
    setPaymentError,
    setView,
    setCheckoutIntentState,
  ]);

  const getCheckoutItems = useCallback((): ProcessCartItem[] => {
    if (cartMode) {
      return cartItems.map((item) => ({
        projectId: item.ProjectId,
        amount: item.Amount,
        quantity: item.Quantity || 1,
        shareId: item.ProjectShareId ?? undefined,
        isRecurring: item.isRecurring,
        recurringDate: item.recurringDate
          ? new Date(item.recurringDate).toISOString()
          : undefined,
        donateTypeId: item.DonateTypeId ?? undefined,
      }));
    }
    if (!projectParams) return [];
    return [
      {
        projectId: projectParams.projectId,
        amount: projectParams.amount,
        quantity: projectParams.quantity || 1,
        shareId: projectParams.shareId,
        isRecurring: projectParams.isRecurring,
        recurringDate: projectParams.recurringDate,
        donateTypeId: projectParams.donateTypeId,
        categoryId: projectParams.categoryId,
        giftInfo: projectParams.giftInfo,
        certificateInfo: projectParams.certificateInfo,
        enterText: projectParams.enterText,
        isGift: projectParams.giftInfo ? true : undefined,
      },
    ];
  }, [cartMode, cartItems, projectParams]);

  useEffect(() => {
    if (
      !isOpen ||
      view !== "payment" ||
      paymentMethodTab !== "bankTransfer" ||
      !bankTransferAvailable ||
      !associationId
    ) {
      return;
    }

    let cancelled = false;
    const fetchBanks = async () => {
      setIsLoadingBanks(true);
      setBankTransferError(null);
      try {
        const response = await bankService.getBanksWithAccounts(associationId);
        if (!cancelled && response.success) {
          const banks = response.data?.banks || [];
          setBanksWithAccounts(banks);
          if (banks.length === 0) {
            setBankTransferError(t("checkout.errors.failedToLoadBanks"));
          }
        }
      } catch (error) {
        if (!cancelled && !isAuthError(error)) {
          setBankTransferError(t("checkout.errors.failedToLoadBanks"));
        }
      } finally {
        if (!cancelled) setIsLoadingBanks(false);
      }
    };

    const fetchSuccessSettings = async () => {
      try {
        const response =
          await paymentService.getDonationSuccessConfig(associationId);
        if (!cancelled && response.success && response.data) {
          setSuccessSettings({
            ...DEFAULT_DONATION_SUCCESS_SETTINGS,
            ...response.data,
          });
        }
      } catch {
        // Configured copy is optional; bundled translations are the fallback.
      }
    };

    void fetchBanks();
    void fetchSuccessSettings();
    return () => {
      cancelled = true;
    };
  }, [isOpen, view, paymentMethodTab, bankTransferAvailable, associationId, t]);

  const bankTransferPendingTitle =
    localizedSetting(successSettings.bankTransferPendingTitle, locale) ||
    t("checkout.confirmation.pending");
  const bankTransferPendingMessage =
    localizedSetting(successSettings.bankTransferPendingMessage, locale) ||
    t("checkout.processing.bankTransferPending");

  const handleBankSelect = useCallback(
    (value: string) => {
      const bankId = Number(value);
      if (!Number.isFinite(bankId) || bankId <= 0) {
        setSelectedBankId(null);
        setSelectedAccountId(null);
        return;
      }
      setSelectedBankId(bankId);
      const bank = banksWithAccounts.find((entry) => entry.bank.id === bankId);
      setSelectedAccountId(bank?.accounts[0]?.id ?? null);
      setBankTransferError(null);
    },
    [banksWithAccounts],
  );

  const handleAccountSelect = useCallback((value: string) => {
    const accountId = Number(value);
    setSelectedAccountId(
      Number.isFinite(accountId) && accountId > 0 ? accountId : null,
    );
    setBankTransferError(null);
  }, []);

  const handleReceiptUpload = useCallback(
    async (file: File) => {
      setIsUploadingReceipt(true);
      setBankTransferError(null);
      try {
        const response = await bankService.uploadReceipt(file);
        if (response.success && response.data?.imageId) {
          setReceiptImageId(response.data.imageId);
          return;
        }
        setBankTransferError(
          response.message || t("checkout.errors.failedToUploadReceipt"),
        );
      } catch (error) {
        if (!isAuthError(error)) {
          setBankTransferError(t("checkout.errors.failedToUploadReceipt"));
        }
      } finally {
        setIsUploadingReceipt(false);
      }
    },
    [t],
  );

  const handleSubmitBankTransfer = useCallback(async () => {
    if (!hasPaymentMethodEnabled || !bankTransferAvailable) return;
    if (!selectedBankId || !selectedAccountId || !receiptImageId) {
      setBankTransferError(t("checkout.errors.bankTransferRequiredFields"));
      return;
    }

    const items = getCheckoutItems();
    if (items.length === 0) {
      setBankTransferError(t("checkout.errors.cartEmpty"));
      return;
    }

    if (requiresShippingAddress && !shippingAddressData) {
      setBankTransferError(t("checkout.shipping.errors.addressRequired"));
      return;
    }

    // Fast (quick-donation) sessions re-confirm the phone like guests.
    const isGuestSingleProject = !!(projectParams && !isFullSession);
    let guestMobile: string | undefined;
    if (isGuestSingleProject) {
      const cleaned = validatePhoneAndSetErrors(form, t);
      if (!cleaned) {
        setBankTransferError(t("auth.mobileRequired"));
        return;
      }
      guestMobile = cleaned;
      setPhoneNumber(cleaned);
    }

    setIsSubmittingBankTransfer(true);
    setBankTransferError(null);
    setCheckoutIntentState("paying");

    try {
      const res = await fetch("/api/payments/process-cart", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          marketingContext: checkoutMarketingContext(),
          associationId: associationId || 0,
          paymentMethodId: EnumPaymentMethods.BankTransfer,
          bankId: selectedBankId,
          accountId: selectedAccountId,
          imageId: receiptImageId,
          donorBankText: donorBankText || undefined,
          donorBankAccountNum: donorBankAccountNum || undefined,
          // Single-project (fast) donations are payload-based: the item was
          // never added to the server cart, so it must travel in the body —
          // and the cart, which this payment never charged, must survive it.
          ...(projectParams
            ? { items, checkoutSource: "standalone" as const }
            : {}),
          ...(isGuestSingleProject
            ? {
                donorMobile: guestMobile,
                donorName:
                  (typeof user?.name === "string" ? user.name : "") ||
                  (typeof user?.Name === "string" ? user.Name : "") ||
                  (typeof user?.FullName === "string" ? user.FullName : "") ||
                  undefined,
              }
            : {}),
          shippingAddress: requiresShippingAddress
            ? (shippingAddressData ?? undefined)
            : undefined,
        }),
      });
      const response = await res.json();
      if (res.status === 401) {
        setCheckoutIntentState("idle");
        setBankTransferError(null);
        setView("phone");
        return;
      }
      if (!response.success || !response.data?.operationCode) {
        setCheckoutIntentState("failed");
        setBankTransferError(
          response.message || t("checkout.errors.failedToProcess"),
        );
        return;
      }

      dispatch({
        type: "SET_OPERATION_CODE",
        operationCode: response.data.operationCode,
        donationId: response.data.donationId,
      });
      dispatch({ type: "SET_PAYMENT_SUCCESS" });
      setCheckoutIntentState("success");
      setIsPendingReviewSuccess(true);
    } catch (error) {
      if (isAuthError(error)) {
        setCheckoutIntentState("idle");
        setBankTransferError(null);
        setView("phone");
        return;
      }
      setCheckoutIntentState("failed");
      setBankTransferError(
        getErrorMessage(error, t("checkout.errors.failedToProcess"), t),
      );
    } finally {
      setIsSubmittingBankTransfer(false);
    }
  }, [
    hasPaymentMethodEnabled,
    bankTransferAvailable,
    selectedBankId,
    selectedAccountId,
    receiptImageId,
    getCheckoutItems,
    t,
    associationId,
    donorBankText,
    donorBankAccountNum,
    requiresShippingAddress,
    shippingAddressData,
    projectParams,
    isFullSession,
    form,
    setPhoneNumber,
    setView,
    user,
  ]);

  // ── Payment: auto-prepare only for single-project paths.
  // Cart intent is prepared explicitly on submit to avoid destructive side-effects on modal open.
  useEffect(() => {
    if (
      view !== "payment" ||
      paymentMethodTab !== "card" ||
      !cardEnabled ||
      paymentPreparedRef.current ||
      total <= 0 ||
      preparedPayment ||
      (requiresShippingAddress && !shippingAddressData)
    )
      return;
    if (
      projectParams &&
      !isFullSession &&
      (!phoneNumber || phoneNumber.trim() === "")
    )
      return;
    if (cartMode) return;

    const initializePayment = async () => {
      dispatch({ type: "SET_PAYMENT_PROCESSING" });
      setCheckoutIntentState("preparing");

      try {
        let opCode: string;
        let payAmount: number;
        let moyasarConfigData: MoyasarInitResponse;

        if (cartMode) {
          await initializeCartPayment();
          return;
        } else if (projectParams) {
          const rawMobile =
            phoneNumber ||
            (typeof user?.mobile === "string" ? user.mobile : "") ||
            (typeof user?.PhoneNumber === "string" ? user.PhoneNumber : "");
          const rawName =
            (typeof user?.name === "string" ? user.name : "") ||
            (typeof user?.Name === "string" ? user.Name : "");
          const result = await prepareSingleProjectWithDonor(
            rawMobile,
            rawName,
          );
          opCode = result.opCode;
          payAmount = result.payAmount;
          moyasarConfigData = result.moyasarConfigData;
        } else {
          throw new Error("No payment parameters provided");
        }

        // Save purchase snapshot for analytics
        if (typeof window !== "undefined") {
          sessionStorage.setItem(
            "checkout:lastPurchase",
            JSON.stringify({
              transaction_id: opCode,
              value: payAmount,
              currency: "SAR",
              phone: phoneNumber || undefined,
              quantity: projectParams?.quantity ?? cartItems.length,
              content_ids: projectParams
                ? [String(projectParams.projectId)]
                : cartItems.map((i) => String(i.ProjectId)),
            }),
          );
        }

        dispatch({ type: "SET_PAYMENT_INITIATING" });
        dispatch({
          type: "SET_PAYMENT_READY",
          operationCode: opCode,
          moyasarConfig: moyasarConfigData,
        });
        setCheckoutIntentState("ready");
        paymentPreparedRef.current = true;
      } catch (err: unknown) {
        if (isAuthError(err)) return;
        console.error("[UnifiedDonationModal] Error:", err);
        setCheckoutIntentState("failed");
        setPaymentError(getErrorMessage(err, t("common.error"), t));
      }
    };

    void initializePayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    view,
    total,
    preparedPayment,
    paymentInitKey,
    paymentMethodTab,
    cardEnabled,
    requiresShippingAddress,
    shippingAddressData,
    phoneNumber,
    isFullSession,
    projectParams,
    cartMode,
    setCheckoutIntentState,
  ]);

  // ── Card form submit: tokenize client-side, then create payment and redirect ─
  // For guest + projectParams, config may be missing until we have phone from form; prepare then pay
  const handleCardSubmit = useCallback(
    async (card: MoyasarTokenizeCardParams) => {
      let configToUse = moyasarConfig;

      if (!configToUse && cartMode) {
        // Guest fast checkout: the phone lives in the payment form, so a
        // faster-login session is established before preparing the cart.
        let mobileOverride: string | undefined;
        if (guestCartPhoneInForm) {
          const cleaned = validatePhoneAndSetErrors(form, t);
          if (!cleaned) return;
          setIsCardSubmitting(true);
          setCardError(null);
          setCardFieldErrors(null);
          try {
            const result = await loginFaster(cleaned);
            if (!result.success) {
              setCardError(resolveLoginErrorMessage(result.message, t));
              setIsCardSubmitting(false);
              return;
            }
          } catch {
            setCardError(t("auth.loginError"));
            setIsCardSubmitting(false);
            return;
          }
          setPhoneNumber(cleaned);
          mobileOverride = cleaned;
        }
        setIsCardSubmitting(true);
        setCardError(null);
        setCardFieldErrors(null);
        try {
          const prepared = await initializeCartPayment(
            mobileOverride
              ? { mobileOverride, skipReadyCheck: true }
              : undefined,
          );
          configToUse = prepared?.moyasarConfigData ?? null;
        } catch (err) {
          if (isAuthError(err)) {
            setCheckoutIntentState("idle");
            setError(getErrorMessage(err, undefined, t));
            setView("phone");
            setIsCardSubmitting(false);
            return;
          }
          setCardError(getErrorMessage(err, t("common.error"), t));
          setCardFieldErrors(null);
          setCheckoutIntentState("failed");
          setIsCardSubmitting(false);
          return;
        }
      } else if (!configToUse && projectParams && !isFullSession) {
        const cleaned = validatePhoneAndSetErrors(form, t);
        if (!cleaned) return;
        paymentPreparedRef.current = true;
        setPhoneNumber(cleaned);
        setIsCardSubmitting(true);
        setCardError(null);
        setCardFieldErrors(null);
        setCheckoutIntentState("preparing");
        try {
          const { opCode, payAmount, moyasarConfigData } =
            await prepareSingleProject(cleaned);
          configToUse = moyasarConfigData;
          dispatch({
            type: "SET_PAYMENT_READY",
            operationCode: opCode,
            moyasarConfig: moyasarConfigData,
          });
          paymentPreparedRef.current = true;
          if (typeof window !== "undefined") {
            sessionStorage.setItem(
              "checkout:lastPurchase",
              JSON.stringify({
                transaction_id: opCode,
                value: payAmount,
                currency: "SAR",
                phone: cleaned,
                quantity: projectParams.quantity ?? 1,
                content_ids: [String(projectParams.projectId)],
              }),
            );
          }
          setCheckoutIntentState("ready");
        } catch (err: unknown) {
          paymentPreparedRef.current = false;
          if (isAuthError(err)) return;
          console.error("[UnifiedDonationModal] Prepare on card submit:", err);
          setCheckoutIntentState("failed");
          setCardError(getErrorMessage(err, t("common.error"), t));
          setCardFieldErrors(null);
          setIsCardSubmitting(false);
          return;
        }
      }

      if (!configToUse) {
        setCardError(t("checkout.errors.failedToProcess"));
        setCardFieldErrors(null);
        return;
      }

      setIsCardSubmitting(true);
      setCardError(null);
      setCardFieldErrors(null);
      setCheckoutIntentState("paying");
      console.info("[CheckoutFlow] checkout.payment.submitted", {
        mode: cartMode ? "cart" : "singleProject",
        paymentMethod: "card",
      });
      try {
        // Moyasar redirects the browser to callback_url after 3DS. It must be a
        // reachable page on THIS frontend (the backend-provided callbackUrl can
        // be an internal/placeholder API host), so build it from the current
        // origin and locale — matching the Apple Pay flow.
        const opCodeForCallback =
          configToUse.metadata?.opCode || operationCode || "";
        const callbackUrl = `${window.location.origin}/${locale}/payments/moyasar/callback?opCode=${encodeURIComponent(
          opCodeForCallback,
        )}`;

        if (requiresTokenizedPayment) {
          const payment = await createSavedCardMoyasarPayment({
            publishableKey: configToUse.publishableKey,
            card,
            amount: configToUse.amount,
            currency: configToUse.currency,
            description: configToUse.description,
            callbackUrl,
            metadata: configToUse.metadata,
          });
          // Best-effort: links the payment to the operation early. The card
          // token is only saved after 3DS (verify/webhook), so a failure here
          // must never stop the donor from reaching the 3DS page.
          try {
            await fetch("/api/payments/moyasar/save-card-created", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                operationCode: opCodeForCallback,
                paymentId: payment.paymentId,
                associationId: configToUse.metadata?.associationId,
              }),
            });
          } catch (notifyError) {
            console.error("[Moyasar save-card-created]", notifyError);
          }
          window.location.href = payment.transactionUrl;
          return;
        }

        const token = await tokenizeCardToMoyasar(
          configToUse.publishableKey,
          card,
        );
        const res = await fetch("/api/payments/moyasar/create-payment", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            token,
            amount: configToUse.amount,
            currency: configToUse.currency,
            description: configToUse.description,
            callbackUrl,
            metadata: configToUse.metadata,
          }),
        });
        const data = (await res.json().catch(() => null)) as {
          success?: boolean;
          transaction_url?: string;
          message?: string;
        } | null;
        if (!data?.success || !data.transaction_url) {
          setCheckoutIntentState("failed");
          setCardError(data?.message || t("checkout.errors.failedToProcess"));
          setCardFieldErrors(null);
          return;
        }
        window.location.href = data.transaction_url;
      } catch (err) {
        console.error("[Card payment]", err);
        setCheckoutIntentState("failed");
        if (err instanceof MoyasarTokenizeError) {
          const hasFieldErrors = Object.keys(err.fieldErrors).length > 0;
          setCardFieldErrors(hasFieldErrors ? err.fieldErrors : null);
          setCardError(hasFieldErrors ? null : err.message);
        } else {
          setCardFieldErrors(null);
          setCardError(
            err instanceof Error
              ? err.message
              : t("checkout.errors.failedToProcess"),
          );
        }
      } finally {
        setIsCardSubmitting(false);
      }
    },
    [
      moyasarConfig,
      t,
      projectParams,
      form,
      prepareSingleProject,
      cartMode,
      guestCartPhoneInForm,
      loginFaster,
      initializeCartPayment,
      setError,
      setPhoneNumber,
      setView,
      setCheckoutIntentState,
      locale,
      operationCode,
      requiresTokenizedPayment,
      isFullSession,
    ],
  );

  // ── Payment retry ───────────────────────────────────────────────────────
  const handleRetry = useCallback(() => {
    paymentPreparedRef.current = false;
    dispatch({ type: "SET_PAYMENT_IDLE" });
    setCheckoutIntentState("idle");
    setPaymentInitKey((k) => k + 1);
  }, []);

  const handleContinueFromShipping = useCallback(() => {
    setShippingError(null);
    if (!validateShippingForm()) return;
    setShippingAddressData(shippingFormData);
    setPaymentMethodTab("card");
  }, [setShippingError, validateShippingForm, shippingFormData]);

  // ── Payment complete ────────────────────────────────────────────────────
  const handlePaymentComplete = useCallback(() => {
    if (operationCode) {
      setCheckoutIntentState("success");
      trackDonation({
        operationCode,
        amount: total,
        currency: "SAR",
        phone: phoneNumber || undefined,
        items: projectParams
          ? [
              {
                projectId: projectParams.projectId,
                projectName:
                  typeof projectParams.projectName === "string"
                    ? projectParams.projectName
                    : undefined,
                categoryId: projectParams.categoryId,
                amount: projectParams.amount,
                quantity: projectParams.quantity,
                isRecurring: projectParams.isRecurring === true || undefined,
              },
            ]
          : cartItems.slice(0, 25).map((i) => ({
              projectId: i.ProjectId,
              projectName: i.ProjectName ?? i.categoryName ?? undefined,
              categoryId: i.CategoryId ?? undefined,
              amount: i.Amount,
              quantity: i.Quantity || 1,
              isProduct: i.IsProduct === true || undefined,
              isGift: i.GiftProject === true || undefined,
              isRecurring: i.isRecurring === true || undefined,
            })),
      });
      onSuccess(operationCode);
    }
  }, [
    operationCode,
    total,
    projectParams,
    cartItems,
    phoneNumber,
    trackDonation,
    onSuccess,
  ]);

  // ── Title per view ──────────────────────────────────────────────────────
  const title = useMemo(() => {
    switch (view) {
      case "phone":
        return cartMode
          ? t("checkout.phoneModalTitle")
          : t("quickDonation.title");
      case "password":
        return t("auth.login");
      case "register":
        return t("auth.register");
      case "set-password":
        return t("auth.setPassword");
      case "enter-otp":
        return t("auth.smsVerification");
      case "reset-password":
        return t("auth.changePassword");
      case "payment":
        return t("checkout.completeDonation");
      default:
        return "";
    }
  }, [view, cartMode, t]);

  useEffect(() => {
    if (view !== "payment") return;
    if (!hasPaymentMethodEnabled) {
      dispatch({
        type: "SET_PAYMENT_ERROR",
        message: t("checkout.errors.failedToProcess"),
      });
    }
  }, [view, hasPaymentMethodEnabled, t]);

  const overview = useMemo(() => {
    if (cartMode) {
      const count = cartItems.length;
      return {
        subtitle: t("checkout.cartDonateSubtitle"),
        title:
          count > 1
            ? t("checkout.cartDonateTitleWithCount", { count })
            : t("checkout.cartDonateTitle"),
        imageSrc: OVERVIEW_FALLBACK_IMAGE,
        imageAlt: t("checkout.cartDonateTitle"),
      };
    }
    if (projectParams) {
      return {
        subtitle: t("checkout.campaignDonateNow"),
        title: projectParams.projectName
          ? String(projectParams.projectName)
          : t("checkout.campaignContribute"),
        imageSrc: projectParams.projectImageUrl ?? OVERVIEW_FALLBACK_IMAGE,
        imageAlt: projectParams.projectName
          ? String(projectParams.projectName)
          : t("checkout.campaignContribute"),
      };
    }
    return {
      subtitle: t("checkout.generalDonateSubtitle"),
      title: t("checkout.generalDonateTitle"),
      imageSrc: OVERVIEW_FALLBACK_IMAGE,
      imageAlt: t("checkout.generalDonateTitle"),
    };
  }, [cartMode, projectParams, cartItems.length, t]);

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <ResponsiveModal open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <ResponsiveModalContent
        showCloseButton={false}
        dialogClassName="z-[2060] bg-white modern-scrollbar sm:max-w-[600px] max-h-[90vh] overflow-y-auto gap-0 p-0 rounded-2xl border-b-4 border-b-primary [&_[data-slot=dialog-close]]:text-gray-400 [&_[data-slot=dialog-close]]:hover:text-gray-600"
        overlayClassName="z-[2055]"
        drawerClassName="border-t-4 border-t-primary"
        data-checkout-intent-state={checkoutIntentState}
      >
        <header className="flex items-center justify-between gap-2 px-6 pt-4 pb-2 mb-4 border-gray-100 ">
          {/* Invisible spacer mirroring the close icon so the title stays truly centered */}
          <span className="size-5 shrink-0" aria-hidden />
          <div className="flex items-center justify-center gap-1.5 flex-1 min-w-0 ">
            {/* <Icon
              icon="fa6-solid:circle-dollar-to-slot"
              className="shrink-0 size-6"
              aria-hidden
            /> */}
            <ResponsiveModalTitle className="text-base font-bold text-gray-900 truncate">
              {title}
            </ResponsiveModalTitle>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="text-sm font-medium text-gray-900 hover:text-gray-700"
          >
            <XIcon className="size-5" />
          </button>
        </header>

        <div className="p-3 md:p-6 pt-0">
          {/* ── Shared content (overview card + error) ───────────────────────── */}
          {false && (
            <div>
              <div className="flex gap-3 rounded-xl bg-background p-4 mb-4 overflow-hidden">
                <div className="w-20 h-20 shrink-0 rounded-lg p-2 overflow-hidden bg-gray-300">
                  <Image
                    src={overview.imageSrc}
                    alt={overview.imageAlt}
                    width={80}
                    height={80}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="flex-1 min-w-0 flex flex-col justify-center ">
                  <p className="text-xs mb-2">{overview.subtitle}</p>
                  <p className="text-sm font-semibold text-gray-900">
                    {overview.title}
                  </p>
                  {total > 0 && (
                    <div className="flex justify-between items-center gap-2 mt-1">
                      <span className=" text-gray-700  font-semibold">
                        {t("checkout.totalAmountLabel")}
                      </span>
                      <span className="inline-flex items-center gap-1 text-lg font-semibold text-primary">
                        {formatInteger(Math.round(total), locale)}
                        <SarIcon className="h-[1em] w-auto shrink-0" />
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
            </div>
          )}

          {/* ── Step content ─────────────────────────────────────────────── */}
          {view === "phone" && (
            <DonationPhoneStep
              form={form}
              cartMode={cartMode}
              onLogin={handleLogin}
              onFastDonation={handleFastDonation}
              onQuickDonation={() => void handleQuickDonation()}
              isCheckingPhone={isCheckingPhone}
              isPreparingPayment={isPreparingPayment}
              isQuickDonating={isQuickDonating}
              error={error}
              showBankTransferHint={bankTransferAvailable}
              t={t}
              phoneOnlyLayout={
                guestPhoneOnlyApplePay || applePayPhoneOnlyActive
              }
            />
          )}

          {(view === "password" ||
            view === "register" ||
            view === "set-password" ||
            view === "enter-otp" ||
            view === "reset-password") && (
            <DonationAuthStep
              view={view}
              phoneNumber={phoneNumber}
              onSuccess={handleAuthSuccess}
              onBack={handleBackToPhone}
              onUserExists={() => setView("password")}
              onViewChange={setView}
              cartMode={cartMode}
              isPreparingPayment={isPreparingPayment}
              t={t}
            />
          )}

          {view === "payment" && (
            <>
              {total > 0 && paymentStatus === "success" ? (
                <DonationSuccessStep
                  operationCode={operationCode}
                  donationId={donationId}
                  onClose={handlePaymentComplete}
                  t={t}
                  title={
                    isPendingReviewSuccess
                      ? bankTransferPendingTitle
                      : undefined
                  }
                  message={
                    isPendingReviewSuccess
                      ? bankTransferPendingMessage
                      : undefined
                  }
                />
              ) : requiresShippingAddress && !shippingAddressData ? (
                <div className="space-y-4">
                  <h3 className="text-lg font-bold text-gray-900">
                    {t("checkout.shipping.title")}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {t("checkout.shipping.description")}
                  </p>
                  <div className="rounded-xl bg-background p-4 space-y-4">
                    <ShippingAddressFields
                      isLoadingCities={isLoadingShippingCities}
                      cities={shippingCities}
                      formData={shippingFormData}
                      formErrors={shippingFormErrors}
                      onInputChange={handleShippingInputChange}
                    />
                    {shippingError && (
                      <Alert variant="destructive">
                        <AlertDescription>{shippingError}</AlertDescription>
                      </Alert>
                    )}
                    <div className="flex justify-end">
                      <Button
                        type="button"
                        onClick={handleContinueFromShipping}
                        disabled={isLoadingShippingCities}
                        className="min-w-36"
                      >
                        {t("checkout.proceedToPayment")}
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <DonationPaymentStep
                  total={total}
                  emptyTotalReason={emptyTotalReason}
                  paymentStatus={paymentStatus}
                  paymentError={paymentError}
                  paymentMethodTab={paymentMethodTab}
                  setPaymentMethodTab={handlePaymentMethodTabChange}
                  moyasarConfig={moyasarConfig}
                  onRetry={handleRetry}
                  onCardSubmit={handleCardSubmit}
                  isCardSubmitting={isCardSubmitting}
                  cardError={cardError}
                  cardFieldErrors={cardFieldErrors}
                  onBack={() => setView("phone")}
                  locale={locale}
                  t={t}
                  projectParams={projectParams}
                  applePayDonorMobile={applePayDonorMobile}
                  applePayDonorName={applePayDonorName}
                  bankTransferEnabled={bankTransferAvailable}
                  cardEnabled={cardEnabled}
                  requiresTokenizedPayment={requiresTokenizedPayment}
                  banksWithAccounts={banksWithAccounts}
                  isLoadingBanks={isLoadingBanks}
                  selectedBankId={selectedBankId}
                  selectedAccountId={selectedAccountId}
                  donorBankText={donorBankText}
                  donorBankAccountNum={donorBankAccountNum}
                  receiptImageId={receiptImageId}
                  bankTransferError={bankTransferError}
                  isUploadingReceipt={isUploadingReceipt}
                  isSubmittingBankTransfer={isSubmittingBankTransfer}
                  onSelectBank={handleBankSelect}
                  onSelectAccount={handleAccountSelect}
                  onDonorBankTextChange={setDonorBankText}
                  onDonorBankAccountNumChange={setDonorBankAccountNum}
                  onUploadReceipt={(file) => {
                    void handleReceiptUpload(file);
                  }}
                  onSubmitBankTransfer={() => {
                    void handleSubmitBankTransfer();
                  }}
                  onApplePaySuccess={onSuccess}
                  onApplePayModalError={(msg) => setError(msg)}
                  onApplePayLoginRequired={() => {
                    setError(null);
                    setApplePayPhoneOnlyActive(true);
                    setView("phone");
                  }}
                  cartMode={cartMode}
                  showPhoneWithCard={
                    !!(projectParams && !isFullSession) || guestCartPhoneInForm
                  }
                  showPhoneWithBank={!!(projectParams && !isFullSession)}
                  form={
                    (projectParams && !isFullSession) || guestCartPhoneInForm
                      ? form
                      : undefined
                  }
                  showCardFormWithoutPreparation={
                    cartMode || !!(projectParams && !isFullSession)
                  }
                />
              )}
            </>
          )}
        </div>
      </ResponsiveModalContent>
    </ResponsiveModal>
  );
}
